import {
  type DealItem,
  ProductType,
  type ValidationError,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';

export const CSV_STEPS = ['ident', 'fiche', 'tva', 'prix', 'logi'] as const;

export type CsvStepId = (typeof CSV_STEPS)[number];

export const isCsvStepId = (value: unknown): value is CsvStepId =>
  typeof value === 'string' && (CSV_STEPS as readonly string[]).includes(value);

export const RULE_TO_STEP = {
  [ValidationRuleId.SkuOrGtinOrShopifyVariantId]: 'ident',
  [ValidationRuleId.DuplicateSku]: 'ident',
  [ValidationRuleId.DuplicateGtin]: 'ident',
  [ValidationRuleId.DuplicateVariant]: 'ident',
  [ValidationRuleId.GtinValidness]: 'ident',
  [ValidationRuleId.UrlOrDescriptionImagesOrDriveLink]: 'fiche',
  [ValidationRuleId.RequiredCategory]: 'tva',
  [ValidationRuleId.VatCoherence]: 'tva',
  [ValidationRuleId.RequiredFields]: 'prix',
  [ValidationRuleId.DiscountConsistency]: 'prix',
  [ValidationRuleId.PricingMarginConsistency]: 'prix',
  [ValidationRuleId.NegativeMargin]: 'prix',
  [ValidationRuleId.PriceCompetitiveness]: 'prix',
  [ValidationRuleId.PsychologicalPriceApplication]: 'prix',
  [ValidationRuleId.RequiredShippingFees]: 'logi',
  [ValidationRuleId.PositiveStock]: 'logi',
} as const satisfies Record<ValidationRuleId, CsvStepId>;

export const REQUIRED_FIELD_STEPS = {
  name: 'ident',
  listPrice: 'prix',
  chooseDiscount: 'prix',
  buyingPrice: 'prix',
  choosePrice: 'prix',
  marginPercentage: 'prix',
  couponExpirationDate: 'fiche',
  couponTerms: 'fiche',
  couponHowToUse: 'fiche',
} as const satisfies Partial<Record<keyof DealItem, CsvStepId>>;

export type RequiredFieldKey = keyof typeof REQUIRED_FIELD_STEPS;

const BASE_REQUIRED_FIELDS: RequiredFieldKey[] = [
  'name',
  'listPrice',
  'chooseDiscount',
  'buyingPrice',
  'choosePrice',
  'marginPercentage',
];

const COUPON_REQUIRED_FIELDS: RequiredFieldKey[] = [
  ...BASE_REQUIRED_FIELDS,
  'couponExpirationDate',
  'couponTerms',
  'couponHowToUse',
];

type RequiredFieldsInput = Pick<DealItem, RequiredFieldKey | 'productType'>;

const isRequiredFieldPresent = (
  field: RequiredFieldKey,
  value: unknown
): boolean => {
  if (value == null) return false;
  if (field === 'listPrice' && value === 0) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const missingRequiredFields = (
  item: RequiredFieldsInput
): RequiredFieldKey[] =>
  (item.productType === ProductType.Coupon
    ? COUPON_REQUIRED_FIELDS
    : BASE_REQUIRED_FIELDS
  ).filter((field) => !isRequiredFieldPresent(field, item[field]));

export const stepsOfError = (
  error: Pick<ValidationError, 'ruleId'>,
  item: RequiredFieldsInput
): CsvStepId[] => {
  if (error.ruleId !== ValidationRuleId.RequiredFields) {
    return [RULE_TO_STEP[error.ruleId]];
  }
  const steps = new Set<CsvStepId>(
    missingRequiredFields(item).map((field) => REQUIRED_FIELD_STEPS[field])
  );
  return steps.size > 0
    ? CSV_STEPS.filter((step) => steps.has(step))
    : [RULE_TO_STEP[ValidationRuleId.RequiredFields]];
};
